import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ANALYTICS_CONSENT_GRANTED_EVENT,
  ANALYTICS_CONSENT_KEY,
  OPEN_ANALYTICS_CONSENT_EVENT,
  clearMarketingCookies,
  getAnalyticsConsentChoice,
  type AnalyticsConsentChoice,
} from '../lib/marketing'

export function AnalyticsConsent() {
  const [choice, setChoice] = useState<AnalyticsConsentChoice | null>(getAnalyticsConsentChoice)
  const [open, setOpen] = useState(() => getAnalyticsConsentChoice() === null)

  useEffect(() => {
    const show = () => setOpen(true)
    window.addEventListener(OPEN_ANALYTICS_CONSENT_EVENT, show)
    return () => window.removeEventListener(OPEN_ANALYTICS_CONSENT_EVENT, show)
  }, [])

  function choose(nextChoice: AnalyticsConsentChoice): void {
    const previousChoice = choice
    try {
      window.localStorage.setItem(ANALYTICS_CONSENT_KEY, nextChoice)
    } catch {
      // The choice still applies to this page if storage is unavailable.
    }
    setChoice(nextChoice)
    setOpen(false)

    if (nextChoice === 'granted') {
      if (previousChoice !== 'granted') {
        window.dispatchEvent(new Event(ANALYTICS_CONSENT_GRANTED_EVENT))
      }
      return
    }

    clearMarketingCookies()
    if (previousChoice === 'granted') window.location.reload()
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-[1000] mx-auto max-w-3xl rounded-2xl border border-gold/30 bg-white p-5 text-navy shadow-2xl sm:inset-x-5 sm:bottom-5 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ah-consent-title"
    >
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
        <div className="flex-1">
          <h2 id="ah-consent-title" className="font-heading text-2xl font-semibold">
            Your privacy choices
          </h2>
          <p className="mt-1 font-body text-sm leading-relaxed text-navy/65">
            We use optional analytics and advertising tools to understand what helps riders and measure our ads. Choose analytics or continue with essential site functions only. Read our{' '}
            <Link className="font-semibold text-gold underline underline-offset-2" to="/privacy-policy">
              privacy policy
            </Link>.
          </p>
        </div>
        <div className="grid gap-2 sm:flex sm:flex-none">
          <button
            type="button"
            className="min-h-[52px] rounded-xl border border-navy/25 px-5 font-body text-sm font-bold text-navy"
            onClick={() => choose('denied')}
          >
            Essential only
          </button>
          <button
            type="button"
            className="min-h-[52px] rounded-xl border border-gold bg-gold px-5 font-body text-sm font-bold text-navy"
            onClick={() => choose('granted')}
          >
            Accept analytics
          </button>
        </div>
      </div>
    </div>
  )
}
